const KEY_PREFIX = '3d-viewer.';
/** Prefix used before the app was renamed; values are migrated once. */
const LEGACY_KEY_PREFIX = 'web-step-viewer.';
const THEME_KEY = KEY_PREFIX + 'theme';
const HATCH_FILL_KEY = KEY_PREFIX + 'hatchFill';
const EDGES_KEY = KEY_PREFIX + 'shadedEdges';
const ORTHO_KEY = KEY_PREFIX + 'orthographic';
const TREE_WIDTH_KEY = KEY_PREFIX + 'treeWidth';
const UNIT_KEY = KEY_PREFIX + 'lengthUnit';
const SHOT_SCALE_KEY = KEY_PREFIX + 'screenshotScale';
const SHOT_TRANSPARENT_KEY = KEY_PREFIX + 'screenshotTransparent';
const TREE_HEIGHT_KEY = KEY_PREFIX + 'treeHeight';
const SETTINGS_PANE_KEY = KEY_PREFIX + 'settingsPane';
const BACKGROUND_KEY = KEY_PREFIX + 'background';

export type ThemeName = 'light' | 'dark';

/** Preset ids for section-cap fill color (persisted). */
export type HatchFillId = 'steel' | 'sand' | 'slate' | 'mint' | 'rose';

export const HATCH_FILL_IDS: HatchFillId[] = ['steel', 'sand', 'slate', 'mint', 'rose'];

let migrated = false;

/** Copy preferences saved under the old app name to the new keys (once). */
function migrateLegacyKeys(store: Storage): void {
  migrated = true;
  try {
    for (const key of [THEME_KEY, HATCH_FILL_KEY, EDGES_KEY, ORTHO_KEY]) {
      const legacy = LEGACY_KEY_PREFIX + key.slice(KEY_PREFIX.length);
      const value = store.getItem(legacy);
      if (value === null) continue;
      if (store.getItem(key) === null) store.setItem(key, value);
      store.removeItem(legacy);
    }
  } catch {
    /* ignore quota / permission errors */
  }
}

function storage(): Storage | null {
  try {
    const store = window.localStorage;
    if (!migrated) migrateLegacyKeys(store);
    return store;
  } catch {
    // Private mode / blocked storage: preferences simply don't persist.
    return null;
  }
}

export function loadTheme(): ThemeName {
  const stored = storage()?.getItem(THEME_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  const prefersDark = typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
}

export function saveTheme(theme: ThemeName): void {
  try {
    storage()?.setItem(THEME_KEY, theme);
  } catch {
    /* ignore quota / permission errors */
  }
}

export function loadHatchFill(): HatchFillId {
  const stored = storage()?.getItem(HATCH_FILL_KEY);
  if (stored && (HATCH_FILL_IDS as string[]).includes(stored)) return stored as HatchFillId;
  return 'steel';
}

export function saveHatchFill(id: HatchFillId): void {
  try {
    storage()?.setItem(HATCH_FILL_KEY, id);
  } catch {
    /* ignore quota / permission errors */
  }
}

export type LightingPreset = 'even' | 'studio' | 'contrast';
export const LIGHTING_PRESETS: LightingPreset[] = ['even', 'studio', 'contrast'];
const LIGHTING_KEY = KEY_PREFIX + 'lighting';

export function loadLightingPreset(): LightingPreset {
  const stored = storage()?.getItem(LIGHTING_KEY);
  if (stored && (LIGHTING_PRESETS as string[]).includes(stored)) return stored as LightingPreset;
  return 'studio';
}

export function saveLightingPreset(preset: LightingPreset): void {
  try {
    storage()?.setItem(LIGHTING_KEY, preset);
  } catch {
    /* private mode: the choice just will not persist */
  }
}

export type DisplayMode = 'shaded' | 'edges' | 'wireframe' | 'xray' | 'hidden';
const DISPLAY_MODES: DisplayMode[] = ['shaded', 'edges', 'wireframe', 'xray', 'hidden'];
const DISPLAY_MODE_KEY = KEY_PREFIX + 'displayMode';

/** Render style; falls back to the older "shaded edges" on/off preference. */
export function loadDisplayMode(): DisplayMode {
  const stored = storage()?.getItem(DISPLAY_MODE_KEY);
  if (stored && (DISPLAY_MODES as string[]).includes(stored)) return stored as DisplayMode;
  return storage()?.getItem(EDGES_KEY) === '1' ? 'edges' : 'shaded';
}

export function saveDisplayMode(mode: DisplayMode): void {
  try {
    storage()?.setItem(DISPLAY_MODE_KEY, mode);
    storage()?.removeItem(EDGES_KEY);
  } catch {
    /* ignore */
  }
}

export function loadOrthographic(): boolean {
  return storage()?.getItem(ORTHO_KEY) === '1';
}

export function saveOrthographic(on: boolean): void {
  try {
    storage()?.setItem(ORTHO_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/** Part tree width in CSS px chosen by dragging its edge; null = automatic. */
export function loadTreeWidth(): number | null {
  const n = Number(storage()?.getItem(TREE_WIDTH_KEY));
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function saveTreeWidth(width: number | null): void {
  try {
    if (width === null) storage()?.removeItem(TREE_WIDTH_KEY);
    else storage()?.setItem(TREE_WIDTH_KEY, String(Math.round(width)));
  } catch {
    /* ignore */
  }
}

/** Part tree height in CSS px chosen by dragging its edge; null = fit content. */
export function loadTreeHeight(): number | null {
  const n = Number(storage()?.getItem(TREE_HEIGHT_KEY));
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function saveTreeHeight(height: number | null): void {
  try {
    if (height === null) storage()?.removeItem(TREE_HEIGHT_KEY);
    else storage()?.setItem(TREE_HEIGHT_KEY, String(Math.round(height)));
  } catch {
    /* ignore */
  }
}

export type LengthUnit = 'mm' | 'in';

/** Display unit for lengths; models are always handled in millimetres internally. */
export function loadLengthUnit(): LengthUnit {
  return storage()?.getItem(UNIT_KEY) === 'in' ? 'in' : 'mm';
}

export function saveLengthUnit(unit: LengthUnit): void {
  try {
    storage()?.setItem(UNIT_KEY, unit);
  } catch {
    /* ignore */
  }
}

export type ScreenshotScale = 1 | 2 | 4;

export interface ScreenshotOptions {
  scale: ScreenshotScale;
  transparent: boolean;
}

export function loadScreenshotOptions(): ScreenshotOptions {
  const scale = Number(storage()?.getItem(SHOT_SCALE_KEY));
  return {
    scale: scale === 1 || scale === 4 ? scale : 2,
    transparent: storage()?.getItem(SHOT_TRANSPARENT_KEY) === '1'
  };
}

export function saveScreenshotOptions(options: ScreenshotOptions): void {
  try {
    storage()?.setItem(SHOT_SCALE_KEY, String(options.scale));
    storage()?.setItem(SHOT_TRANSPARENT_KEY, options.transparent ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/**
 * Which Settings section was open last. Stored as the pane's element id; an id
 * that no longer exists is ignored by the caller, so renaming a pane is safe.
 */
export function loadSettingsPane(): string | null {
  return storage()?.getItem(SETTINGS_PANE_KEY) ?? null;
}

export function saveSettingsPane(paneId: string): void {
  try {
    storage()?.setItem(SETTINGS_PANE_KEY, paneId);
  } catch {
    /* ignore */
  }
}

/**
 * Viewport background: `'theme'` follows light/dark as before, anything else
 * is a `#rrggbb` the user picked and keeps in both themes.
 */
export type BackgroundChoice = 'theme' | string;

export function isBackgroundColor(value: string): boolean {
  return /^#[0-9a-f]{6}$/i.test(value);
}

export function loadBackground(): BackgroundChoice {
  const stored = storage()?.getItem(BACKGROUND_KEY);
  if (!stored) return 'theme';
  return stored === 'theme' || isBackgroundColor(stored) ? stored : 'theme';
}

export function saveBackground(value: BackgroundChoice): void {
  try {
    storage()?.setItem(BACKGROUND_KEY, value);
  } catch {
    /* ignore */
  }
}
